import React from 'react';

function Summary({ summary }) {
  if (!summary) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <>
      <div className="summary-section">
        <h2>Overview</h2>
        {summary.combinedTotalAmountDue > 0 && (
          <div className="combined-total-card">
            <h3>Combined Total Amount Due</h3>
            <div className="amount" style={{ fontSize: '2.5em', color: '#667eea', fontWeight: 'bold' }}>
              {formatCurrency(summary.combinedTotalAmountDue)}
            </div>
            <p style={{ textAlign: 'center', marginTop: '10px', color: '#666', fontSize: '0.9em' }}>
              Across all {(summary.statementDetails?.length || Object.keys(summary.summaryByCard).length)} credit cards
            </p>
          </div>
        )}
        <div className="summary-grid">
          <div className="summary-card">
            <h3>Total Expenses</h3>
            <div className="amount">{formatCurrency(summary.totalExpense)}</div>
          </div>
          <div className="summary-card">
            <h3>Number of Categories</h3>
            <div className="amount">{Object.keys(summary.summaryByCategory).length}</div>
          </div>
          <div className="summary-card">
            <h3>Credit Cards</h3>
            <div className="amount">{(summary.statementDetails?.length || Object.keys(summary.summaryByCard).length)}</div>
          </div>
        </div>
      </div>

      {summary.statementDetails && summary.statementDetails.length > 0 && (
        <div className="summary-section">
          <h2>Statement Breakdown by Card</h2>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Credit Card</th>
                  <th className="amount-cell">Total Amount Due</th>
                </tr>
              </thead>
              <tbody>
                {summary.statementDetails.map((detail, idx) => (
                  <tr key={idx}>
                    <td>{detail.card}</td>
                    <td className="amount-cell">{formatCurrency(detail.totalDue)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  <td>COMBINED TOTAL</td>
                  <td className="amount-cell">{formatCurrency(summary.combinedTotalAmountDue || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {summary.calculationBreakdown && (
        <div className="summary-section">
          <h2>Calculation Check</h2>
          <div className="table-container">
            <table>
              <tbody>
                <tr>
                  <td>Total Debits</td>
                  <td className="amount-cell">{formatCurrency(summary.calculationBreakdown.totalDebits || 0)}</td>
                </tr>
                <tr>
                  <td>Total Credits (excluding repayment credits)</td>
                  <td className="amount-cell">- {formatCurrency(summary.calculationBreakdown.totalCredits || 0)}</td>
                </tr>
                <tr>
                  <td>Ignored Repayment Credits</td>
                  <td className="amount-cell">{formatCurrency(summary.calculationBreakdown.ignoredRepaymentCredits || 0)}</td>
                </tr>
                <tr>
                  <td><strong>Net Total Due</strong></td>
                  <td className="amount-cell"><strong>{formatCurrency(summary.calculationBreakdown.netTotalDue || 0)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="summary-section">
        <h2>Expenses by Category</h2>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th className="amount-cell">Amount</th>
                <th className="amount-cell">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(summary.summaryByCategory)
                .sort((a, b) => b[1] - a[1])
                .map(([category, amount]) => (
                <tr key={category}>
                  <td>{category}</td>
                  <td className="amount-cell">{formatCurrency(amount)}</td>
                  <td className="amount-cell">
                    {summary.totalExpense !== 0 ? ((amount / summary.totalExpense) * 100).toFixed(1) : '0.0'}%
                  </td>
                </tr>
              ))}
              <tr style={{ fontWeight: 'bold', borderTop: '2px solid #667eea', backgroundColor: '#f0f0ff' }}>
                <td>TOTAL</td>
                <td className="amount-cell">{formatCurrency(summary.totalExpense)}</td>
                <td className="amount-cell">100.0%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="summary-section">
        <h2>Expenses by Credit Card</h2>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Credit Card</th>
                <th className="amount-cell">Amount</th>
                <th className="amount-cell">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(summary.summaryByCard).map(([card, amount]) => (
                <tr key={card}>
                  <td>{card}</td>
                  <td className="amount-cell">{formatCurrency(amount)}</td>
                  <td className="amount-cell">
                    {summary.totalExpense !== 0 ? ((amount / summary.totalExpense) * 100).toFixed(1) : '0.0'}%
                  </td>
                </tr>
              ))}
              <tr style={{ fontWeight: 'bold', borderTop: '2px solid #667eea', backgroundColor: '#f0f0ff' }}>
                <td>TOTAL</td>
                <td className="amount-cell">{formatCurrency(summary.totalExpense)}</td>
                <td className="amount-cell">100.0%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="summary-section">
        <h2>Category Breakdown by Card</h2>
        {Object.entries(summary.summaryByCardAndCategory).map(([card, categories]) => {
          const cardTotal = summary.summaryByCard[card] || 0;
          return (
            <div key={card} style={{ marginBottom: '30px' }}>
              <h3 style={{ color: '#667eea', marginBottom: '15px' }}>{card}</h3>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th className="amount-cell">Amount</th>
                      <th className="amount-cell">% of Card Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(categories)
                      .sort((a, b) => b[1] - a[1])
                      .map(([category, amount]) => (
                        <tr key={`${card}-${category}`}>
                          <td>{category}</td>
                          <td className="amount-cell">{formatCurrency(amount)}</td>
                          <td className="amount-cell">
                            {cardTotal !== 0 ? ((amount / cardTotal) * 100).toFixed(1) : '0.0'}%
                          </td>
                        </tr>
                      ))}
                    <tr style={{ fontWeight: 'bold', borderTop: '2px solid #667eea', backgroundColor: '#f0f0ff' }}>
                      <td>TOTAL</td>
                      <td className="amount-cell">{formatCurrency(cardTotal)}</td>
                      <td className="amount-cell">100.0%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default Summary;
